import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { solidWhenReducedTransparency } from '../styles/reducedTransparency';
import LogoAnimation from './LogoAnimation';
import MultiDynaMiteAnimation from './MultiDynaMiteAnimation';
import { LiquidGlassCard, LiquidGlassButton } from './LiquidGlass/index';
import { heroGlassEffect, heroHoverEffect } from './LiquidGlass/LiquidGlassStyles';
import DynamicPageWrapper from './DynamicPageWrapper';
import { useResponsiveLayout } from '../config/componentLayout';
import {
  HERO_TITLE_LEAD,
  HERO_TITLE_HIGHLIGHT,
  HERO_TITLE_TAIL,
  HERO_PARAGRAPH,
} from '../config/heroContent';

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

/**
 * DWEB-25 CHANGE 1 — the h1 glow is now STATIC.
 *
 * This keyframe animated `text-shadow`, which is not a compositable property,
 * so it repainted the largest text on the page on the MAIN THREAD every frame,
 * forever. Measured on live v6.1.2 it was the only one of six infinite
 * keyframes doing paint work, and the prime suspect for why Lighthouse could
 * never reach a settled state — GTmetrix failed outright with "Analysis Error —
 * Error generating Lighthouse report" (job Qh6iyx3X).
 *
 * The shadow is KEPT at the keyframe's own rest value (its 0%/100% stop), so
 * the headline looks identical standing still; it just stops pulsing. A static
 * text-shadow is painted once and costs nothing.
 *
 * Approved by the owner. Do NOT substitute an opacity-based pulse — the point
 * is that nothing on the h1 animates forever.
 */
const HERO_TITLE_GLOW = '0 0 10px rgba(255,255,255,0.3)';


/**
 * DWEB-176 (v6.9.0) — THE HERO'S FILL, AND THE ONE PLACE IT IS STATED.
 *
 * The hero read blue for one value: it filled itself with
 * `rgba(15, 23, 42, 0.8)` where every other glass surface uses the same navy
 * at `0.38`. Same hue, more than twice the alpha — against the black ground
 * DWEB-170 established, that is what reads as blue. `saturate(300%)` was near
 * inert here (there is no chroma in the backdrop but the ambient wash), so the
 * blue was the FILL, not the filter.
 *
 * Underneath it sat DWEB-172's shape a third time: Hero.js carried a PRIVATE
 * glass definition — its own background, backdrop-filter and border — while
 * `heroGlassEffect`, written and reviewed for exactly this surface, went
 * unused. The private copy was also THINNER than the shared one (one flat 2px
 * border at 0.15; no per-edge lighting, no inset rims, no depth gradient, no
 * sheen) and made up the difference with tint. The hero should be the
 * thickest glass on the page, not the bluest.
 *
 * So the glass now comes from `heroGlassEffect` / `heroHoverEffect` and the
 * fill is declared once, here.
 *
 * 🔴 THE SOLID BELOW IS ARITHMETIC ON THIS CONSTANT, NOT A FREE VALUE.
 * `solidWhenReducedTransparency` takes HERO_FILL composited over the black
 * ground; change one and the other is wrong, and e2e/glass-tint.spec.js pins
 * the pair. They are declared adjacent so they cannot drift apart silently.
 */
const HERO_FILL = 'rgba(0, 0, 0, 0.8)';
/* rgba(0,0,0,0.8) over the black ground is rgb(0,0,0) — so under
   prefers-reduced-transparency the hero's fill is the ground's own colour and
   the surface is carried entirely by the lit rim, the shadows and the sheen,
   none of which is transparency. That is the intended reading of this option,
   not a degenerate case of it. */
const HERO_FILL_SOLID = 'rgb(0, 0, 0)';

// HERO CONTAINER - the page's thickest glass, via the shared hero style
const HeroContainer = styled(LiquidGlassCard)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  color: white;
  overflow: hidden;
  padding: 40px;
  margin: 0;

  /* The glass itself — blur, border, rims, depth gradient and sheen — is
     stated in LiquidGlass/LiquidGlassStyles.js and nowhere else. Hero.js
     declares the fill and its own layout; it states no glass values. */
  ${heroGlassEffect}
  background: ${HERO_FILL};
  ${heroHoverEffect}

  @media (max-width: 768px) {
    padding: 30px;
  }

  ${solidWhenReducedTransparency(HERO_FILL_SOLID)}
`;

// NESTED ELEMENT 1: Particle animation container within liquid glass
const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3; /* Above liquid glass effects, below content */
  border-radius: inherit;
  overflow: hidden;
`;

// NESTED ELEMENT 2: Logo container within liquid glass
const LogoContainer = styled.div`
  position: absolute;
  top: 80px;
  left: 40px;
  z-index: 10; /* Above particle animation and liquid glass effects */
  padding: 0;
  background: transparent;
  border: none;
  ${css`animation: ${floatAnimation} 8s ease-in-out infinite;`}
  
  @media (max-width: 1024px) {
    top: 60px;
    left: 30px;
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: 30px;
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

// NESTED ELEMENT 3: Content wrapper within liquid glass
const ContentWrapper = styled.div`
  text-align: center;
  z-index: 10; /* Above particle animation and liquid glass effects */
  max-width: 900px;
  padding: 0 10px;
  margin: 0 auto;
  ${css`animation: ${floatAnimation} 6s ease-in-out infinite;`}
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  /* Ensure content is always below logo container */
  margin-top: 200px; /* Space for logo on desktop */

  @media (max-width: 1024px) {
    margin-top: 180px; /* Adjust for smaller logo on tablet */
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 10px;
    margin-top: 0; /* Logo is relative positioned on mobile, so no top margin needed */
  }
  
  @media (max-width: 480px) {
    padding: 0 5px;
  }
`;

const AIHighlight = styled.span`
  background: linear-gradient(135deg, #60A5FA 0%, #A78BFA 50%, #34D399 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
`;

const StyledTitle = styled.h1`
  font-size: 4.5rem;
  margin: 0 0 20px 0;
  letter-spacing: 2px;
  font-weight: 800;
  line-height: 1.1;
  text-align: center;
  /*
    && is REQUIRED, not stylistic. HeroContainer extends LiquidGlassCard, which
    styles descendant headings — ".cdFSCa h1 { text-shadow: none }" at
    specificity (0,1,1). A plain ".hvwgCq" rule is (0,1,0) and LOSES to it, so
    the static shadow silently would not paint.

    This did not show up while the glow was animated because CSS animations
    outrank normal declarations in the cascade entirely — the keyframe applied
    over the top of the reset. Swapping the animation for a static declaration
    is exactly the change that drops it back into the losing side of a
    specificity contest. Measured: computed text-shadow was "none" with the
    plain rule, and the ancestor reset was found by enumerating every matching
    rule rather than by reading the file.

    && doubles the class to (0,2,0), which wins.
  */
  && {
    text-shadow: ${HERO_TITLE_GLOW};
  }
  
  /* Gradient text with proper fallback */
  background: linear-gradient(to right, #fff, #E2E8F0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  /* Fallback for browsers that don't support background-clip: text */
  @supports not (-webkit-background-clip: text) {
    color: white;
    background: none;
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
    margin: 0 0 15px 0;
    line-height: 1.2;
  }
  
  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

/**
 * The live-demo callout (WEBSITE_COPY_DRAFT.md section 6, DWEB-48). Sits under
 * the CTAs as a hero footnote: the chatbot IS the thesis, so the claim belongs
 * where the claim is made, not buried near the launcher.
 */
const LiveDemoCallout = styled.p`
  color: #e5e7eb;
  font-size: 1rem;
  max-width: 720px;
  margin: 28px auto 0;
  line-height: 1.6;
  text-align: center;
  font-weight: 400;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6);

  strong {
    color: #F59E0B;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    max-width: 90%;
    margin-top: 20px;
  }
`;

const StyledParagraph = styled.p`
  color: white;
  font-size: 1.4rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 10px rgba(30, 64, 175, 0.6), 0 0 20px rgba(30, 64, 175, 0.3);
  text-align: center;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 20px;
    max-width: 90%;
    line-height: 1.6;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 15px;
  }
`;


function Hero() {
  const layout = useResponsiveLayout('hero');
  const [headerVisible, setHeaderVisible] = useState(false);

  // PERF: Throttle ref to limit event handler calls
  const lastMouseMoveTime = useRef(0);
  const THROTTLE_MS = 100;

  useEffect(() => {
    const checkHeaderVisibility = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isAtTop = scrollTop < 50;
      setHeaderVisible(isAtTop);
    };

    const handleScroll = () => {
      checkHeaderVisibility();
    };

    // PERF: Throttled mousemove handler to reduce unnecessary state updates
    const handleMouseMove = (e) => {
      const now = Date.now();
      if (now - lastMouseMoveTime.current < THROTTLE_MS) return;
      lastMouseMoveTime.current = now;

      const { clientY, clientX } = e;
      const windowWidth = window.innerWidth;
      const topMiddleZone = {
        top: 0,
        bottom: 100,
        left: windowWidth * 0.3,
        right: windowWidth * 0.7
      };

      if (clientY >= topMiddleZone.top &&
        clientY <= topMiddleZone.bottom &&
        clientX >= topMiddleZone.left &&
        clientX <= topMiddleZone.right) {
        setHeaderVisible(true);
      }
    };

    checkHeaderVisibility();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <DynamicPageWrapper
      id="hero"
      componentName="hero"
      backgroundKey="hero-section"
      containerProps={{
        style: {
          minHeight: '100vh',
          padding: '0',
          paddingTop: '4rem',
          marginTop: headerVisible ? layout.marginBottom : '0'
        }
      }}
    >
      <HeroContainer variant="auto">
        {/* Multiple DynaMite Avatar Animations - replaces particle animation */}
        <ParticleContainer>
          <MultiDynaMiteAnimation />
        </ParticleContainer>

        {/* Logo as nested element within hero glass */}
        <LogoContainer>
          <LogoAnimation />
        </LogoContainer>

        {/* Content as nested element within hero glass */}
        <ContentWrapper>
          {/* Hero F1, locked 2026-08-24. WEBSITE_COPY_DRAFT.md section 1,
              verbatim. AIHighlight now accents "production reality" — the new
              heading has no "AI" in it, and the accent is what carries the
              promise. textContent is unchanged by the span, so the landmark
              assertion still reads one string. */}
          <StyledTitle>{HERO_TITLE_LEAD}<AIHighlight>{HERO_TITLE_HIGHLIGHT}</AIHighlight>{HERO_TITLE_TAIL}</StyledTitle>
          <StyledParagraph>{HERO_PARAGRAPH}</StyledParagraph>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <LiquidGlassButton
              variant="primary"
              glassVariant="auto"
              size="large"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openChat', {
                  detail: {
                    source: 'hero-button'
                  }
                }));
              }}
            >
              Chat with DynaMite
            </LiquidGlassButton>

            <LiquidGlassButton
              variant="secondary"
              glassVariant="auto"
              size="large"
              onClick={() => {
                // First try to scroll to existing demo sections on the page
                const appDemosSection = document.getElementById('app-demos');
                const contactSection = document.getElementById('contact');

                if (appDemosSection) {
                  // Scroll to app demos section which shows design capabilities
                  appDemosSection.scrollIntoView({ behavior: 'smooth' });
                } else if (contactSection) {
                  // Fallback to contact section
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  // If no sections exist, trigger a custom event that could be handled by the app
                  window.dispatchEvent(new CustomEvent('showDesignDemo', {
                    detail: {
                      source: 'hero-explore-button'
                    }
                  }));
                  console.log('Design demo event dispatched - no demo sections found on page');
                }
              }}
            >
              See what I ship
            </LiquidGlassButton>
          </div>

          <LiveDemoCallout>
            <strong>The receptionist is live on this page:</strong> DynaMite, in the corner, is the AI Phone Receptionist offer running in text — the same hosted agent that answers the phone line, from the same knowledge base.
          </LiveDemoCallout>
        </ContentWrapper>
      </HeroContainer>
    </DynamicPageWrapper>
  );
}

export default Hero;
